import React, { useCallback, useMemo } from 'react';
import {
    AggregatorFn,
    HeaderGroup,
    Row,
    useBlockLayout,
    useExpanded,
    useGlobalFilter,
    useGroupBy,
    useSortBy,
    useTable,
    useRowSelect,
} from 'react-table';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import * as naturalCompare from 'string-natural-compare';
import { useDeepCompareMemo } from 'use-deep-compare';

import {
    Checkbox,
    CompactNumberCell,
    GlobalFilter,
    TableHeader,
    TableHeaderGroup,
    TableFooterGroup,
    TruncatedTextCell,
} from '@trqk/components';
import {
    createFuzzyTextFilter,
    getScrollbarWidth,
    getGroupByMatchState,
    locale,
} from '@trqk/utils';

import {
    COLUMN_IDS,
    defaultColumnWidth,
    rowHeight,
    subRowHeight,
    tableHeight,
    summarizeRowsAbove,
} from '../constants';
import {
    AggregatedDetectionsCell,
    AggregatedGapCell,
    AggregatedStatementWorkCell,
    AggregatedTruncatedTextCell,
    GapCell,
    GroupedWorkNameCell,
    IncludeWorkCell,
    PerformancesCell,
    PlaceholderWorkNameCell,
    StatementWorkCell,
    WorkEstimatedLossCell,
    WorkNameCell,
} from './cell';
import {
    WorkDetectionsFooter,
    WorkEstimatedLossFooter,
    WorkGapFooter,
    WorkIncludeFooter,
    WorkNameFooter,
    WorkPerformancesFooter,
    WorkStatementWorkFooter,
} from './footer';
import { CustomGroupedRows } from './row';
import {
    aggregateGap,
    aggregateIncludeWork,
    aggregateNumberRange,
    aggregatePerformances,
    aggregateStatementWork,
    getGapWorkStatementWorkCellValue,
    sortByIncludedGap,
} from '../utils';
import { useGapAnalysisTools } from '../../../../../../../hooks';

const groupByMatchState =
    getGroupByMatchState<TrqkGapChannelAudio>(summarizeRowsAbove);

interface ConfirmWorksTableProps {
    /**
     * Raw table data
     */
    data: TrqkGapChannelAudio[];
    /**
     * Initial state of row expansion. Each key is a row ID, and values of `true` indicate that the
     * row is initially expanded.
     */
    initialExpanded: Record<string, boolean>;
    /**
     * Initial state of row selection. Each key is a row ID, and values of `true` indicate that the
     * row is initially selected.
     */
    initialSelected: Record<string, boolean>;
    /**
     * Callback function to update the matched_work_id for a single {@link TrqkGapChannelAudio}
     */
    updateAudioMatch: UpdateAudioMatch;
}

const {
    enUS: {
        MANAGE_GAP_ANALYSIS: {
            STEPS: {
                CONFIRM_WORKS: { COLUMN_HEADERS },
            },
        },
    },
} = locale;

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Renders a table of audio files for a given channel during creation of a Gap Analysis report. The
 * table allows the user to select individual audio files to include in the report.
 *
 * @remarks
 * This table has some complicated logic because of how we calculate gaps. Each gapAudio represents
 * a single detected audio file. Several of these gapAudios can be matched to the same statement
 * work. A "gap" compares the detections reported by the ACR (i.e. TuneSat or BMAT) for this audio
 * file to the performances reported by the PRO (i.e. ASCAP, BMI or SESAC). If there is a difference
 * between what the ACR found by detection and what the PRO reported for the same time period, we
 * call this a "gap".
 * The rows in the rendered table need to reflect that multiple gapAudios can be matched to the same
 * statement work. We pass the `groupByMatchState` function as the `groupByFn` to the table instance
 * to do this. This means that gapAudios that share the same `matched_statement_id` are grouped
 * into an array of subRows. Typically, you would collapse / expand these subRows with a nested
 * row layout in the UI. However, it gets even trickier here, because we don't want to
 * display a nested layout for an array of subRows that only contains one element. From the user
 * perspective, it doesn't make sense to collapse those subRows into a nested row. This is what
 * the constant `summarizeRowsAbove` is used for; it indicates that any collection of subRows
 * longer than this value (currently set at 1) should be summarized in the UI into a nested row
 * that can be expanded / collapsed. From a practical standpoint, this value will probably always be
 * 1, but since we need to use it across several locations, it is set as a constant to prevent
 * issues if we ever need to change it.
 */
export const ConfirmWorksTable = ({
    data,
    initialExpanded,
    initialSelected,
    updateAudioMatch,
}: ConfirmWorksTableProps): JSX.Element => {
    const { context, send } = useGapAnalysisTools();
    const { currentChannelId, channels, statementId } = context;
    const someMatchedWorkIdWasEdited = currentChannelId
        ? context.edits?.[currentChannelId]?.someMatchedWorkIdWasEdited ?? false
        : false;

    const channelNameDetection = useDeepCompareMemo(() => {
        const channel = channels.find(ch => ch.channel_id === currentChannelId);

        return channel?.channel_name_detection ?? null;
    }, [channels, currentChannelId]);

    const detectionSource =
        context?.trqkGap?.detection_batch?.detection_source ?? 'TRQK';
    const history = useHistory();
    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const {
        location: { search },
    } = history;

    const aggregations = useMemo(
        (): Record<string, AggregatorFn<TrqkGapChannelAudio>> => ({
            aggregateGap,
            aggregateIncludeWork,
            aggregateNumberRange,
            aggregatePerformances,
            aggregateStatementWork,
        }),
        [],
    );

    const columns = useMemo(
        () => [
            {
                id: COLUMN_IDS.WORK_NAME,
                accessor: ({
                    audio_filename,
                    detection_artist,
                    detection_title,
                }: TrqkGapChannelAudio): GapWorkNameCellValue => ({
                    audioFilename: audio_filename ?? null,
                    detectionArtist: detection_artist ?? null,
                    detectionTitle: detection_title ?? null,
                }),
                sortType: (
                    rowA: Row<TrqkGapChannelAudio>,
                    rowB: Row<TrqkGapChannelAudio>,
                ): number =>
                    naturalCompare(
                        rowA?.values?.[COLUMN_IDS.WORK_NAME]?.detectionTitle ??
                            rowA.values?.[COLUMN_IDS.WORK_NAME]
                                ?.audioFilename ??
                            '',
                        rowB?.values?.[COLUMN_IDS.WORK_NAME]?.detectionTitle ??
                            rowB.values?.[COLUMN_IDS.WORK_NAME]
                                ?.audioFilename ??
                            '',
                    ),

                aggregate: 'count',
                Cell: WorkNameCell,
                Grouped: GroupedWorkNameCell,
                Placeholder: PlaceholderWorkNameCell,
                headerLabel: COLUMN_HEADERS.WORK_NAME[detectionSource],
                Header: TableHeader,
                Footer: WorkNameFooter,
                width: 220,
            },
            {
                id: COLUMN_IDS.STATEMENT_WORK,
                accessor: (
                    audio: TrqkGapChannelAudio,
                ): GapWorkStatementWorkCellValue =>
                    getGapWorkStatementWorkCellValue(audio),
                aggregate: 'aggregateStatementWork',
                Cell: StatementWorkCell,
                Aggregated: AggregatedStatementWorkCell,
                headerLabel: COLUMN_HEADERS.STATEMENT_WORK,
                Header: TableHeader,
                Footer: WorkStatementWorkFooter,
                width: 210,
                updateAudioMatch,
                disableSortBy: true,
            },
            {
                id: COLUMN_IDS.SURVEY_TYPE,
                accessor: ({
                    matched_work_id,
                    match_options,
                }: TrqkGapChannelAudio): Maybe<string> =>
                    matched_work_id
                        ? match_options.find(
                              option => option.song_id === matched_work_id,
                          )?.work_survey_type ?? null
                        : null,
                sortType: (
                    rowA: Row<TrqkGapChannelAudio>,
                    rowB: Row<TrqkGapChannelAudio>,
                ): number =>
                    naturalCompare(
                        rowA?.values?.[COLUMN_IDS.SURVEY_TYPE] ?? '',
                        rowB?.values?.[COLUMN_IDS.SURVEY_TYPE] ?? '',
                    ),
                Cell: TruncatedTextCell,
                Aggregated: AggregatedTruncatedTextCell,
                headerLabel: COLUMN_HEADERS.SURVEY_TYPE,
                Header: TableHeader,
                width: 60,
            },
            {
                id: COLUMN_IDS.USE_CODE,
                accessor: ({
                    matched_work_id,
                    match_options,
                }: TrqkGapChannelAudio): Maybe<string> =>
                    matched_work_id
                        ? match_options.find(
                              option => option.song_id === matched_work_id,
                          )?.work_use_code ?? null
                        : null,
                sortType: (
                    rowA: Row<TrqkGapChannelAudio>,
                    rowB: Row<TrqkGapChannelAudio>,
                ): number =>
                    naturalCompare(
                        rowA?.values?.[COLUMN_IDS.USE_CODE] ?? '',
                        rowB?.values?.[COLUMN_IDS.USE_CODE] ?? '',
                    ),
                Cell: TruncatedTextCell,
                Aggregated: AggregatedTruncatedTextCell,
                headerLabel: COLUMN_HEADERS.USE_CODE,
                Header: TableHeader,
                width: 60,
            },
            {
                id: COLUMN_IDS.DETECTIONS,
                accessor: ({ detection_count }: TrqkGapChannelAudio): number =>
                    detection_count ?? 0,
                aggregateValue: (
                    value: number,
                    row: Row<TrqkGapChannelAudio>,
                ) => {
                    if (row.isSelected) {
                        return value;
                    }

                    return 0;
                },
                Aggregated: AggregatedDetectionsCell,
                aggregate: 'sum',
                Cell: CompactNumberCell,
                headerLabel: COLUMN_HEADERS.DETECTIONS,
                Header: TableHeader,
                Footer: WorkDetectionsFooter,
                sortDescFirst: true,
                width: 65,
            },
            {
                id: COLUMN_IDS.GAP,
                accessor: ({
                    detection_count,
                    matched_work_id,
                    match_options,
                }: TrqkGapChannelAudio): number => {
                    const matchedOption =
                        match_options.find(
                            option => option.song_id === matched_work_id,
                        ) ?? null;

                    if (matchedOption === null) {
                        // We can't actually calculate a gap when there is no matched work, so we
                        // return 0
                        return 0;
                    }
                    const detectionCount = detection_count ?? 0;
                    const matchedWorkPerformances =
                        matchedOption?.work_performances ?? 0;

                    return detectionCount - matchedWorkPerformances;
                },
                aggregate: 'aggregateGap',
                sortType: 'sortByIncludedGap',
                // Return the whole row to the aggregateGap function
                aggregateValue: (
                    value: number,
                    // This is the original row value, so we can't tell if it is currently selected
                    // or not, which means we can't aggregate the gap based on the included
                    // detections. This is why the AggregatedGapCell does conditional rendering
                    // based on the number of subRows, and also why the WorkGapFooter has some
                    // custom logic for finding the gap sum.
                    row: Row<TrqkGapChannelAudio>,
                ): Row<TrqkGapChannelAudio> => row,
                Aggregated: AggregatedGapCell,
                Cell: GapCell,
                headerLabel: COLUMN_HEADERS.GAP,
                Header: TableHeader,
                Footer: WorkGapFooter,
                sortDescFirst: true,
                width: 50,
            },
            {
                id: COLUMN_IDS.PERFORMANCES,
                accessor: ({
                    matched_work_id,
                    matched_work_title,
                    match_options,
                }: TrqkGapChannelAudio): GapWorkPerformancesCellValue => {
                    const performances = matched_work_id
                        ? match_options.find(
                              option => option.song_id === matched_work_id,
                          )?.work_performances ?? 0
                        : 0;

                    return {
                        performances,
                        channelNameDetection,
                        matchedWorkTitle: matched_work_title ?? null,
                        statementId,
                    };
                },
                aggregate: 'aggregatePerformances',
                Cell: PerformancesCell,
                headerLabel: COLUMN_HEADERS.PERFORMANCES,
                Header: TableHeader,
                Footer: WorkPerformancesFooter,
                sortDescFirst: true,
                width: 60,
            },
            {
                id: COLUMN_IDS.ESTIMATED_LOSS,
                accessor: ({
                    estimated_loss_max,
                    estimated_loss_min,
                }: TrqkGapChannelAudio): NumberRange => ({
                    max: estimated_loss_max ?? 0,
                    min: estimated_loss_min ?? 0,
                }),
                sortDescFirst: true,
                sortType: (
                    rowA: Row<TrqkGapChannelAudio>,
                    rowB: Row<TrqkGapChannelAudio>,
                ): number => {
                    const estimatedLossMaxA =
                        rowA?.values?.[COLUMN_IDS.ESTIMATED_LOSS].max ?? 0;
                    const estimatedLossMaxB =
                        rowB?.values?.[COLUMN_IDS.ESTIMATED_LOSS].max ?? 0;

                    if (estimatedLossMaxA > estimatedLossMaxB) {
                        return 1;
                    }

                    if (estimatedLossMaxA < estimatedLossMaxB) {
                        return -1;
                    }

                    return 0;
                },
                aggregate: 'aggregateNumberRange',
                Cell: WorkEstimatedLossCell,
                headerLabel: COLUMN_HEADERS.ESTIMATED_LOSS,
                Header: TableHeader,
                Footer: WorkEstimatedLossFooter,
                width: 90,
            },
            {
                id: COLUMN_IDS.INCLUDE_AUDIO_FILE,
                accessor: ({
                    gap_audio_id,
                }: TrqkGapChannelAudio): GapWorkIncludeWorkCellValue => ({
                    channelId: currentChannelId,
                    gapAudioId: gap_audio_id,
                }),
                aggregate: 'aggregateIncludeWork',
                Cell: IncludeWorkCell,
                Footer: WorkIncludeFooter,
                headerLabel: COLUMN_HEADERS.INCLUDE_AUDIO_FILE,
                Header: TableHeader,
                sortType: (
                    rowA: Row<TrqkGapChannelAudio>,
                    rowB: Row<TrqkGapChannelAudio>,
                ): number => {
                    const rowASelected = rowA.isSelected;
                    const rowBSelected = rowB.isSelected;

                    if (rowASelected && !rowBSelected) {
                        return 1;
                    }

                    if (rowBSelected && !rowASelected) {
                        return -1;
                    }

                    const checkedIsTie =
                        (rowASelected && rowBSelected) ||
                        (!rowASelected && !rowBSelected);

                    const rowATitle =
                        rowA?.values?.[COLUMN_IDS.WORK_NAME]?.detectionTitle ??
                        null;
                    const rowBTitle =
                        rowB.values?.[COLUMN_IDS.WORK_NAME]?.detectionTitle ??
                        null;

                    if (
                        checkedIsTie &&
                        rowATitle !== null &&
                        rowBTitle !== null
                    ) {
                        return naturalCompare(rowBTitle, rowATitle);
                    }

                    return 0;
                },
                sortDescFirst: true,
                send,
                width: 75,
            },
        ],
        [
            currentChannelId,
            channelNameDetection,
            data,
            search,
            send,
            updateAudioMatch,
            statementId,
        ],
    );

    const filterTypes = useMemo(
        () => ({
            globalFilter: createFuzzyTextFilter<PerformanceTableItem>({
                keys: [`values.${COLUMN_IDS.WORK_NAME}.detectionTitle`],
            }),
        }),
        [],
    );

    const groupByFn = useCallback(groupByMatchState, []);

    const getRowId = useCallback(
        (
            item: TrqkGapChannelAudio,
            relativeIndex: number,
            parent?: Row<TrqkGapChannelAudio>,
        ): string =>
            parent
                ? [parent.id, item.gap_audio_id].join('.')
                : item.gap_audio_id,
        [],
    );

    const sortTypes = useMemo(
        () => ({
            sortByIncludedGap,
        }),
        [],
    );

    const {
        footerGroups,
        getTableBodyProps,
        getTableProps,
        headerGroups,
        prepareRow,
        setGlobalFilter,
        state,
        totalColumnsWidth,
        flatRows,
        selectedFlatRows,
        isAllRowsSelected,
        toggleAllRowsSelected,
    } = useTable<TrqkGapChannelAudio>(
        {
            aggregations,
            columns,
            data,
            defaultColumn,
            filterTypes,
            getRowId,
            globalFilter: filterTypes.globalFilter,
            groupByFn,
            initialState: {
                expanded: initialExpanded,
                groupBy: [COLUMN_IDS.WORK_NAME],
                selectedRowIds: initialSelected,
                sortBy: [
                    {
                        id: COLUMN_IDS.GAP,
                        desc: true,
                    },
                ],
            },
            sortTypes,
            autoResetPage: false,
            autoResetExpanded: false,
            autoResetGroupBy: false,
            autoResetSelectedRows: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetGlobalFilter: false,
            autoResetRowState: false,
        },
        useBlockLayout,
        useGlobalFilter,
        useGroupBy,
        useSortBy,
        useExpanded,
        useRowSelect,
    );

    const scrollBarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <>
            {/*
                It feels a bit hacky, but this absolute positioning lets us keep the <GlobalFilter>
                in this component while conserving on vertical space. The `maxWidth` props set on
                the <LabeledValue>s in <DataSources> will prevent an overlap. Also, there is a width
                in the <Breadcrumbs> component that will also keep the breadcrumbs from overlapping
                the <GlobalFilter>.
            */}
            <div className="absolute">
                <div
                    className="relative"
                    style={{ top: -86, left: 590, width: 300 }}
                >
                    <GlobalFilter
                        globalFilter={state.globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        placeholder={
                            locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.CONFIRM_WORKS
                                .GLOBAL_FILTER_PLACEHOLDER
                        }
                    />
                    <div className="pt-2" style={{ paddingLeft: 190 }}>
                        <Checkbox
                            inputId="select-all-works"
                            checked={isAllRowsSelected}
                            indeterminate={
                                !isAllRowsSelected &&
                                selectedFlatRows.length > 0
                            }
                            label={
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .CONFIRM_WORKS.SELECT_ALL
                            }
                            labelClasses="text-sm"
                            size="large"
                            onChange={e => {
                                const target = e.target as HTMLInputElement;

                                if (currentChannelId) {
                                    toggleAllRowsSelected(target.checked);

                                    send({
                                        type: 'TOGGLE_ALL_GAP_AUDIOS_CHECKED',
                                        value: {
                                            channelId: currentChannelId,
                                            checked: target.checked,
                                        },
                                    });
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            {/*
                z-10 is necessary to keep mouse functionality because the headers will otherwise be
                stacked underneath the <div>s that wrap GlobalFilter above
             */}
            <div {...getTableProps()} className="z-10 font-normal trqk-table">
                <div>
                    {headerGroups.map(
                        (
                            headerGroup: HeaderGroup<TrqkGapChannelAudio>,
                        ): JSX.Element => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();

                            return (
                                <TableHeaderGroup<TrqkGapChannelAudio>
                                    key={headerGroupKey}
                                    headerGroup={headerGroup}
                                />
                            );
                        },
                    )}
                </div>
                <div className="overflow-x-hidden overflow-y-auto respond-to-mouse">
                    <div
                        {...getTableBodyProps()}
                        style={{
                            height: tableHeight,
                            width: totalColumnsWidth + scrollBarWidth,
                        }}
                    >
                        <CustomGroupedRows
                            flatRows={flatRows}
                            subRowHeight={subRowHeight}
                            rowHeight={rowHeight}
                            prepareRow={prepareRow}
                        />
                    </div>
                </div>
                <div
                    className={cx(
                        'font-semibold text-sm border border-tertiary-400',
                        {
                            'text-highlight-magenta':
                                selectedFlatRows.length === 0,
                            'text-tertiary-300 italic':
                                someMatchedWorkIdWasEdited,
                        },
                    )}
                >
                    {footerGroups.map(
                        (
                            footerGroup: HeaderGroup<TrqkGapChannelAudio>,
                        ): JSX.Element => {
                            const { key: footerGroupKey } =
                                footerGroup.getFooterGroupProps();

                            return (
                                <TableFooterGroup<TrqkGapChannelAudio>
                                    key={footerGroupKey}
                                    footerGroup={footerGroup}
                                />
                            );
                        },
                    )}
                </div>
            </div>
        </>
    );
};

// ConfirmWorksTable.whyDidYouRender = true;
